import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { useAuth } from '../../../contexts/AuthContext';


const SignupForm = ({ onSwitchToLogin }) => {
  const navigate = useNavigate();
  const { signUp, signInWithGoogle } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    role: 'free'
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const roleOptions = [
    { value: 'free', label: 'Free User' },
    { value: 'retail', label: 'Retail User' },
    { value: 'enterprise', label: 'Enterprise Client' },
    { value: 'partner', label: 'Retail Partner' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleRoleChange = (value) => {
    setFormData(prev => ({ ...prev, role: value }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.fullName?.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!formData?.email) {
      newErrors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/?.test(formData?.password)) {
      newErrors.password = 'Password must contain uppercase, lowercase, and number';
    }

    if (!formData?.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);

    try {
      const { data, error } = await signUp(formData?.email, formData?.password, {
        full_name: formData?.fullName,
        role: formData?.role
      });
      
      if (error) {
        setErrors({
          email: error?.message || 'Registration failed. Please try again.'
        });
      } else if (data?.user) {
        // Navigate to appropriate dashboard based on role
        const dashboardRoutes = {
          retail: '/retail-user-dashboard',
          enterprise: '/enterprise-client-dashboard',
          partner: '/retail-partner-dashboard',
          admin: '/super-administrator-dashboard',
          free: '/retail-user-dashboard',
          premium: '/retail-user-dashboard'
        };
        navigate(dashboardRoutes?.[formData?.role] || '/retail-user-dashboard');
      }
    } catch (error) {
      setErrors({
        email: 'An error occurred. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    setLoading(true);
    try {
      const { error } = await signInWithGoogle();
      if (error) {
        setErrors({
          email: error?.message || 'Google sign-up failed. Please try again.'
        });
      }
    } catch (error) {
      setErrors({
        email: 'An error occurred. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-2">Create Account</h2>
        <p className="text-sm text-muted-foreground">Sign up to get started with FINVISAR</p>
      </div>

      <Input
        label="Full Name"
        type="text"
        name="fullName"
        placeholder="Enter your full name"
        value={formData?.fullName}
        onChange={handleInputChange}
        error={errors?.fullName}
        required
        disabled={loading}
      />

      <Input
        label="Email Address"
        type="email"
        name="email"
        placeholder="Enter your email"
        value={formData?.email}
        onChange={handleInputChange}
        error={errors?.email}
        required
        disabled={loading}
      />

      <Input
        label="Password"
        type="password"
        name="password"
        placeholder="Create a password"
        value={formData?.password}
        onChange={handleInputChange}
        error={errors?.password}
        required
        disabled={loading}
      />

      <Input
        label="Confirm Password"
        type="password"
        name="confirmPassword"
        placeholder="Confirm your password"
        value={formData?.confirmPassword}
        onChange={handleInputChange}
        error={errors?.confirmPassword}
        required
        disabled={loading}
      />

      <Select
        label="Account Type"
        value={formData?.role}
        onChange={handleRoleChange}
        options={roleOptions}
        disabled={loading}
      />

      <Button
        type="submit"
        variant="default"
        fullWidth
        loading={loading}
        iconName="UserPlus"
        iconPosition="right"
        className="bg-gradient-to-r from-fintech-blue-500 to-fintech-purple-500 hover:from-fintech-blue-600 hover:to-fintech-purple-600 shadow-glow-blue"
      >
        Create Account
      </Button>

      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-fintech-blue-200"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-4 bg-white text-muted-foreground">Or sign up with</span>
        </div>
      </div>

      <Button
        type="button"
        variant="outline"
        fullWidth
        onClick={handleGoogleSignup}
        disabled={loading}
        iconName="Mail"
        iconPosition="left"
        className="border-fintech-blue-300 hover:bg-fintech-blue-50 hover:border-fintech-blue-500 transition-smooth"
      >
        Continue with Google
      </Button>

      <div className="text-center">
        <p className="text-sm text-muted-foreground">
          Already have an account?{' '}
          <button
            type="button"
            onClick={onSwitchToLogin}
            className="text-fintech-blue-600 hover:text-fintech-purple-600 transition-smooth font-medium"
            disabled={loading}
          >
            Sign in
          </button>
        </p>
      </div>
    </form>
  );
};

export default SignupForm;